import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';

import { useApplicationState } from '@atlas/core';
import { NotificationTopic, TransportManager } from '@atlas/transport';

import { bonusStatuses, bonusTypes } from 'common/constants';
import useBonuses from 'common/Hooks/useBonuses';
import { sortBonusesByAvailabilityAndAmount } from 'src/common/utils/sortBonusesByAvailabilityAndAmount';

export const DepositBonusContext = createContext({
  activateBonus: () => {},
  activatedBonus: null,
  activeBonuses: [],
  amountToGetBonus: 0,
  bonusToShow: null,
  bonusesList: [],
  depositAmount: undefined,
  depositBonuses: [],
  depositBonusesToShow: [],
  intendedBonusRef: {
    current: null,
  },
  isBonusesDisabled: false,
  isLoading: false,
  selectedBonus: null,
  setDepositAmount: () => {},
  setSelectedBonus: () => {},
  wallet: {},
});
export const useDepositBonusContext = () => useContext(DepositBonusContext);

const { DEPOSIT, NO_DEPOSIT, RELOAD_DEPOSIT, RELOAD_NO_DEPOSIT } = bonusTypes;
const { ACTIVATED, IN_PROGRESS } = bonusStatuses;
const OPPOSITE_PRODUCT_TYPE = {
  CASINO: 'SPORTSBOOK',
  SPORTSBOOK: 'CASINO',
};

const checkBonusTypeFits = bonusType => [DEPOSIT, RELOAD_DEPOSIT].includes(bonusType);
const getActivationSortValue = bonus => bonus?.createTime ?? bonus?.id ?? 0;

export const DepositBonusProvider = ({ children, paymentMethod }) => {
  const { activateBonus, activeBonuses, bonuses, isLoading, wallet } = useBonuses();
  const applicationState = useApplicationState();

  const [depositAmount, setDepositAmount] = useState();
  const [selectedBonus, setSelectedBonus] = useState(null);
  const [isBonusesDisabled, setIsBonusesDisabled] = useState(false);
  const [depositCompleteBonus, setDepositCompleteBonus] = useState(null);
  const [depositCompletePaymentMethodId, setDepositCompletePaymentMethodId] = useState(null);
  const [
    hasPaymentMethodChangedSinceDepositComplete,
    setHasPaymentMethodChangedSinceDepositComplete,
  ] = useState(false);
  const [activatedBonusPaymentMethodId, setActivatedBonusPaymentMethodId] = useState(null);
  const intendedBonusRef = useRef(null);
  const trackedActivatedBonusIdRef = useRef(null);

  const { currency } = wallet;
  const country = applicationState?.account?.country || null;

  const depositBonuses = useMemo(() => {
    const activeDepositBonuses = activeBonuses
      .filter(bonus => {
        const { bonus: { bonusType } = {}, status } = bonus;
        return checkBonusTypeFits(bonusType) && status === ACTIVATED;
      })
      .map(bonus => {
        const financeData = bonus?.bonus.financeDataList.find(item => item.currency === currency);
        return { ...bonus, ...bonus.bonus, financeData, id: bonus.id };
      })
      .sort(sortBonusesByAvailabilityAndAmount);

    const availableDepositBonuses = bonuses
      .filter(bonus => checkBonusTypeFits(bonus.bonusType))
      .map(bonus => {
        const financeData = bonus?.financeDataList.find(item => item.currency === currency);
        return { ...bonus, financeData };
      })
      .sort(sortBonusesByAvailabilityAndAmount);

    return [...activeDepositBonuses, ...availableDepositBonuses];
  }, [bonuses, activeBonuses, currency]);

  const isBonusAllowed = useCallback(
    bonus => {
      const normalizeBonus = bonus?.bonus ? bonus.bonus : bonus;

      return (
        !normalizeBonus.basicLimit?.notAllowedPaymentMethods?.includes(
          paymentMethod?.id?.toString(),
        ) && !normalizeBonus?.basicLimit?.notAllowedCountries?.includes(country)
      );
    },
    [country, paymentMethod?.id],
  );
  const normalizeBonus = useCallback(
    bonus => {
      if (!bonus) return null;

      const normalizedBonus = bonus?.bonus ?? bonus;
      const financeData =
        normalizedBonus?.financeDataList?.find(item => item.currency === currency) ??
        bonus?.financeData;
      const normalizedBonusWithFinanceData = {
        ...bonus,
        ...normalizedBonus,
        financeData,
        id: bonus?.id ?? normalizedBonus?.id,
      };

      return {
        ...normalizedBonusWithFinanceData,
        disabled: !isBonusAllowed(normalizedBonusWithFinanceData),
      };
    },
    [currency, isBonusAllowed],
  );

  const bonusToShow = depositBonuses[0];

  const internalActivatedBonus = useMemo(() => {
    const latestActivatedBonus = activeBonuses
      .filter(({ bonus: { bonusType } = {}, status }) => {
        return checkBonusTypeFits(bonusType) && status === ACTIVATED;
      })
      .reduce((latestBonus, currentBonus) => {
        if (!latestBonus) return currentBonus;

        return getActivationSortValue(currentBonus) >= getActivationSortValue(latestBonus)
          ? currentBonus
          : latestBonus;
      }, null);

    if (!latestActivatedBonus) return undefined;

    return normalizeBonus(latestActivatedBonus);
  }, [activeBonuses, normalizeBonus]);

  const hasPaymentMethodChangedAfterActivation =
    activatedBonusPaymentMethodId != null && activatedBonusPaymentMethodId !== paymentMethod?.id;

  const activatedBonus = useMemo(() => {
    if (
      hasPaymentMethodChangedAfterActivation &&
      OPPOSITE_PRODUCT_TYPE[internalActivatedBonus?.productType]
    ) {
      return null;
    }

    return internalActivatedBonus;
  }, [hasPaymentMethodChangedAfterActivation, internalActivatedBonus]);

  const depositBonusesToShow = useMemo(() => {
    const typesToHide = activeBonuses
      .filter(
        active =>
          active.status === IN_PROGRESS &&
          (active.bonus.bonusType === NO_DEPOSIT ||
            active.bonus.bonusType === RELOAD_NO_DEPOSIT ||
            active.bonus.productType === 'CASINO' ||
            active.bonus.productType === 'SPORTSBOOK'),
      )
      .map(active => active.bonus.productType);

    const filteredBonuses = typesToHide.length
      ? depositBonuses.filter(bonus => !typesToHide.includes(bonus.productType))
      : depositBonuses;

    const bonusesToShow = filteredBonuses.map(bonus => ({
      ...bonus,
      ...bonus.bonus,
      disabled: !isBonusAllowed(bonus),
      id: bonus.id,
    }));

    return [
      ...bonusesToShow.filter(bonus => !bonus.disabled),
      ...bonusesToShow.filter(bonus => bonus.disabled),
    ];
  }, [activeBonuses, depositBonuses, isBonusAllowed]);

  useEffect(() => {
    if (!internalActivatedBonus) {
      trackedActivatedBonusIdRef.current = null;
      setActivatedBonusPaymentMethodId(null);
      return;
    }

    if (trackedActivatedBonusIdRef.current !== internalActivatedBonus.id) {
      trackedActivatedBonusIdRef.current = internalActivatedBonus.id;
      setActivatedBonusPaymentMethodId(paymentMethod?.id ?? null);
    }
  }, [internalActivatedBonus, paymentMethod?.id]);

  useEffect(() => {
    const handleDepositComplete = completedBonusData => {
      const completedBonus =
        completedBonusData ||
        internalActivatedBonus ||
        selectedBonus ||
        intendedBonusRef.current ||
        null;

      if (!completedBonus) {
        return;
      }

      intendedBonusRef.current = completedBonus;
      setSelectedBonus(completedBonus);
      setDepositCompleteBonus(completedBonus);
      setDepositCompletePaymentMethodId(paymentMethod?.id ?? null);
      setHasPaymentMethodChangedSinceDepositComplete(false);
      setIsBonusesDisabled(true);
    };

    const findBonusByTransportPayload = ({ bonusId, bonusInstanceId }) => {
      const matchedActiveBonus = activeBonuses.find(({ bonus, id }) => {
        return (
          checkBonusTypeFits(bonus?.bonusType) && (id === bonusInstanceId || bonus?.id === bonusId)
        );
      });

      if (matchedActiveBonus) {
        return normalizeBonus(matchedActiveBonus);
      }

      const matchedAvailableBonus = bonuses.find(bonus => {
        return checkBonusTypeFits(bonus?.bonusType) && bonus.id === bonusId;
      });

      return normalizeBonus(matchedAvailableBonus);
    };

    const unsubscribe = TransportManager.getInstance().subscribe(
      {
        notificationTopic: NotificationTopic.BONUS_INSTANCE_STATUS_CHANGED,
      },
      data => {
        const completedBonus = findBonusByTransportPayload(data);

        handleDepositComplete(completedBonus);
      },
    );

    return unsubscribe;
  }, [
    activeBonuses,
    bonuses,
    internalActivatedBonus,
    normalizeBonus,
    paymentMethod?.id,
    selectedBonus,
  ]);

  useEffect(() => {
    if (
      depositCompletePaymentMethodId == null ||
      hasPaymentMethodChangedSinceDepositComplete ||
      paymentMethod?.id == null
    ) {
      return;
    }

    if (depositCompletePaymentMethodId !== paymentMethod.id) {
      setHasPaymentMethodChangedSinceDepositComplete(true);
    }
  }, [
    depositCompletePaymentMethodId,
    hasPaymentMethodChangedSinceDepositComplete,
    paymentMethod?.id,
  ]);

  useEffect(() => {
    if (internalActivatedBonus) {
      setDepositCompleteBonus(null);
      setDepositCompletePaymentMethodId(null);
      setHasPaymentMethodChangedSinceDepositComplete(false);
    }
  }, [internalActivatedBonus]);

  useEffect(() => {
    if (internalActivatedBonus) {
      setSelectedBonus(internalActivatedBonus.disabled ? null : internalActivatedBonus);
      setIsBonusesDisabled(true);
      intendedBonusRef.current = internalActivatedBonus.disabled ? null : internalActivatedBonus;
      return;
    }

    const firstAvailableBonus = depositBonusesToShow.find(bonus => !bonus.disabled) || null;
    const getAllowedBonus = bonus =>
      depositBonusesToShow.find(item => item.id === bonus?.id && !item.disabled) || null;

    if (intendedBonusRef.current && !getAllowedBonus(intendedBonusRef.current)) {
      intendedBonusRef.current = null;
    }

    setSelectedBonus(prev => {
      if (prev) {
        return getAllowedBonus(prev) || firstAvailableBonus;
      }

      if (intendedBonusRef.current || !firstAvailableBonus) {
        return null;
      }

      return firstAvailableBonus;
    });
  }, [internalActivatedBonus, depositBonusesToShow]);

  useEffect(() => {
    if (internalActivatedBonus || depositCompleteBonus) {
      setIsBonusesDisabled(true);
      return;
    }

    const currentAmount = Number(depositAmount);
    const activeOrSelected = selectedBonus || internalActivatedBonus;
    const bonusToCheck = intendedBonusRef.current || activeOrSelected;

    if (!bonusToCheck) return;

    const minDeposit =
      bonusToCheck.financeData?.minDeposit ??
      bonusToCheck.financeDataList?.find(item => item.currency === currency)?.minDeposit ??
      0;

    if (depositAmount === '' || depositAmount === null || depositAmount === undefined) {
      setIsBonusesDisabled(false);
      return;
    }

    if (currentAmount > 0 && currentAmount < minDeposit) {
      if (selectedBonus) {
        intendedBonusRef.current = selectedBonus;
        setSelectedBonus(null);
      }
      setIsBonusesDisabled(true);
    } else if (currentAmount >= minDeposit) {
      if (intendedBonusRef.current) {
        setSelectedBonus(intendedBonusRef.current);
        intendedBonusRef.current = null;
      }
      setIsBonusesDisabled(false);
    }
  }, [depositAmount, selectedBonus, internalActivatedBonus, depositCompleteBonus, currency]);

  const handleSetSelectedBonus = useCallback(
    bonus => {
      if (!hasPaymentMethodChangedSinceDepositComplete) {
        setDepositCompleteBonus(null);
        setDepositCompletePaymentMethodId(null);
        setHasPaymentMethodChangedSinceDepositComplete(false);
      }

      if (!bonus) {
        intendedBonusRef.current = null;
        setSelectedBonus(null);
        return;
      }

      if (isBonusesDisabled) {
        intendedBonusRef.current = bonus;
        setSelectedBonus(null);
      } else {
        intendedBonusRef.current = null;
        setSelectedBonus(bonus);
      }
    },
    [hasPaymentMethodChangedSinceDepositComplete, isBonusesDisabled],
  );

  const amountToGetBonus = useMemo(() => {
    const amount = Number(depositAmount);
    if (!depositAmount || Number.isNaN(amount)) return 0;

    const bonus = internalActivatedBonus || selectedBonus || intendedBonusRef.current;
    if (!bonus) return 0;

    const minDeposit =
      bonus.financeData?.minDeposit ??
      bonus.financeDataList?.find(item => item.currency === currency)?.minDeposit ??
      0;

    return Math.max(minDeposit - amount, 0);
  }, [internalActivatedBonus, currency, selectedBonus, depositAmount]);

  const bonusesList = useMemo(() => {
    const oppositeProductType =
      (hasPaymentMethodChangedAfterActivation &&
        OPPOSITE_PRODUCT_TYPE[internalActivatedBonus?.productType]) ||
      (hasPaymentMethodChangedSinceDepositComplete &&
        OPPOSITE_PRODUCT_TYPE[depositCompleteBonus?.productType]);

    if (oppositeProductType) {
      return depositBonusesToShow.filter(bonus => bonus.productType === oppositeProductType);
    }

    if (internalActivatedBonus) {
      return [{ ...internalActivatedBonus }];
    }
    if (depositCompleteBonus) {
      return [{ ...depositCompleteBonus }];
    }

    return depositBonusesToShow;
  }, [
    depositBonusesToShow,
    hasPaymentMethodChangedAfterActivation,
    depositCompleteBonus,
    hasPaymentMethodChangedSinceDepositComplete,
    internalActivatedBonus,
  ]);

  const value = useMemo(
    () => ({
      activateBonus,
      activatedBonus,
      activeBonuses,
      amountToGetBonus,
      bonusToShow,
      bonusesList,
      depositAmount,
      depositBonuses,
      depositBonusesToShow,
      intendedBonusRef,
      isBonusesDisabled,
      isLoading,
      selectedBonus,
      setDepositAmount,
      setSelectedBonus: handleSetSelectedBonus,
      wallet,
    }),
    [
      activateBonus,
      activatedBonus,
      activeBonuses,
      amountToGetBonus,
      bonusToShow,
      bonusesList,
      depositAmount,
      depositBonuses,
      depositBonusesToShow,
      handleSetSelectedBonus,
      isBonusesDisabled,
      isLoading,
      selectedBonus,
      wallet,
    ],
  );

  return <DepositBonusContext.Provider value={value}>{children}</DepositBonusContext.Provider>;
};

DepositBonusProvider.propTypes = {
  children: PropTypes.node.isRequired,
  paymentMethod: PropTypes.shape({
    id: PropTypes.number,
  }),
};
